import {callLambda} from '@/lib/api/lambda';
import {CURRENCY_MAPPING} from '@/constant/language';
import {CURRENCY} from '@/constant/currency';
import {CURRENCY_MARKET_MAPPING} from '@/constant/market';

import i18n from '@/i18n/index.js';

/**
 * getCurrencyByLocale
 * @param {string} locale - locale
 * @returns {currency} promise
 */
const getCurrencyByLocale = (locale) => {
  return CURRENCY_MAPPING[locale] || CURRENCY.USD;
};

/**
 * getMarketId
 * @returns {string} marketId
 */
const getMarketId = () => {
  const currency = getCurrencyByLocale(i18n.locale);
  return (
    CURRENCY_MARKET_MAPPING[currency] || CURRENCY_MARKET_MAPPING[CURRENCY.USD]
  );
};

/**
 * getMarketSalePages
 * @returns {Promise} promise
 */
export async function getMarketSalePages() {
  const resolvedMarketIdOrHandle = getMarketId();
  const res = await callLambda({
    category: 'ec',
    method: 'get',
    apiName: `markets/${resolvedMarketIdOrHandle}/salePages`,
    params: {
      withProducts: true,
    },
  });
  return res.data;
}

/**
 * getMarketCategories
 * @returns {Promise} promise
 */
export async function getMarketCategories() {
  const resolvedMarketIdOrHandle = getMarketId();
  const res = await callLambda({
    category: 'ec',
    method: 'get',
    apiName: `markets/${resolvedMarketIdOrHandle}/categories`,
  });
  return res.data;
}

/**
 * getMarketSalePage
 * @param {string} marketSalePageId marketSalePageId
 * @returns {Promise} promise
 */
export async function getMarketSalePage(marketSalePageId) {
  const resolvedMarketIdOrHandle = getMarketId();

  const res = await callLambda({
    category: 'ec',
    method: 'get',
    apiName: `markets/${resolvedMarketIdOrHandle}/salePages/${marketSalePageId}`,
    params: {
      withProducts: true,
      withConfig: true,
      detail: true,
    },
  });
  return res.data;
}

/**
 * Fetch products for a target market sale page.
 * @param {Object} params - Action parameters.
 * @param {string} params.currency - currency
 * @param {string} params.username - username
 * @param {string} params.marketSalePageId - Optional sale page identifier or handle.
 * @param {string} params.productId - Optional product identifier or handle.
 * @returns {Promise<void>} Resolves when the API request is completed.
 */
export async function getSalePageProduct({
  currency,
  username,
  marketSalePageId,
  productId,
}) {
  const params = {};
  const resolvedMarketIdOrHandle = getMarketId();

  if (currency) params.currency = currency;
  if (username) params.buyer = username;
  const res = await callLambda({
    category: 'ec',
    method: 'get',
    apiName: `markets/${resolvedMarketIdOrHandle}/salePages/${marketSalePageId}/products/${productId}`,
    params,
  });
  return res.data;
}
/**
 * 取得商品列表
 * @returns {Promise} promise
 */
export async function getProducts() {
  const res = await callLambda({
    category: 'ec',
    method: 'get',
    apiName: 'products',
  });
  return res.data;
}

/**
 * 檢查是否可以建立訂單
 * @param {Object} data request payload
 * @returns {Promise} promise
 */
export async function checkOrderViability(data) {
  const res = await callLambda({
    category: 'ec',
    apiName: 'checkOrderViability',
    method: 'post',
    data,
  });
  return res.data;
}

/**
 * 創建訂單
 * @param {Object} data request payload
 * @returns {Promise} promise
 */
export async function createOrder(data) {
  const res = await callLambda({
    category: 'ec',
    apiName: 'order',
    method: 'post',
    data,
  });
  return res.data;
}

/**
 * 檢查訂單狀態
 * @returns {Promise} promise
 */
export async function checkOrderState({id, platform, detail = false}) {
  const res = await callLambda({
    category: 'ec',
    apiName: 'checkOrderState',
    method: 'post',
    data: {
      id,
      platform,
      detail,
    },
  });
  return res.data;
}

export default {
  getProducts,
  getMarketSalePages,
  getMarketCategories,
  getMarketSalePage,
  getSalePageProduct,
  createOrder,
  checkOrderState,
  checkOrderViability,
};
